// Un .xlsx mínimo, sin dependencias. Un .xlsx es un zip con unos pocos XML; el
// zip va sin comprimir («stored»), que todos los lectores aceptan y ahorra una
// librería de compresión. Lo justo para el libro de la gestoría: varias hojas,
// texto, números con dos decimales, fechas de verdad y cabecera en negrita.
// Las librerías habituales (SheetJS, ExcelJS) pesan cientos de KB para esto.

export type Celda =
  | string
  | number
  | null
  | { fecha: string } // AAAA-MM-DD → fecha de Excel, se ve DD/MM/AAAA
  | { negrita: string | number };

export interface Hoja {
  nombre: string;
  anchos: number[]; // en caracteres, uno por columna
  filas: Celda[][]; // la primera fila es la cabecera (en negrita y fija)
  /** Columnas (desde 0) cuyos números van con dos decimales. */
  decimales?: number[];
}

const enc = new TextEncoder();

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function columna(n: number): string {
  let s = '';
  for (n += 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** Días desde el 30/12/1899: así guarda Excel las fechas. */
function serialExcel(iso: string): number | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return (Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000;
}

// Estilos (índices de cellXfs): 0 normal · 1 negrita · 2 número · 3 fecha · 4 número en negrita
const ESTILOS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="#,##0.00"/><numFmt numFmtId="165" formatCode="dd/mm/yyyy"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="164" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

function celdaXml(ref: string, c: Celda, cabecera: boolean, conDecimales: boolean): string {
  if (c === null || c === '') return '';
  const texto = (s: string, estilo: number) =>
    `<c r="${ref}" t="inlineStr"${estilo ? ` s="${estilo}"` : ''}><is><t xml:space="preserve">${esc(s)}</t></is></c>`;
  const numero = (n: number, estilo: number) => `<c r="${ref}"${estilo ? ` s="${estilo}"` : ''}><v>${n}</v></c>`;
  if (typeof c === 'string') return texto(c, cabecera ? 1 : 0);
  if (typeof c === 'number') return numero(c, cabecera ? 1 : conDecimales ? 2 : 0);
  if ('fecha' in c) {
    const n = serialExcel(c.fecha);
    return n === null ? texto(c.fecha, 0) : numero(n, 3);
  }
  return typeof c.negrita === 'number' ? numero(c.negrita, conDecimales ? 4 : 1) : texto(c.negrita, 1);
}

function hojaXml(h: Hoja): string {
  const dec = new Set(h.decimales ?? []);
  const filas = h.filas
    .map((fila, i) => {
      const celdas = fila.map((c, j) => celdaXml(`${columna(j)}${i + 1}`, c, i === 0, dec.has(j))).join('');
      return `<row r="${i + 1}">${celdas}</row>`;
    })
    .join('');
  const cols = h.anchos.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols>${cols}</cols><sheetData>${filas}</sheetData></worksheet>`;
}

export function crearXlsx(hojas: Hoja[]): Uint8Array {
  const n = hojas.length;
  const ficheros: [string, string][] = [
    ['[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${hojas.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
</Types>`],
    ['_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`],
    ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${hojas.map((h, i) => `<sheet name="${esc(h.nombre.slice(0, 31))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>
</workbook>`],
    ['xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${hojas.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('\n')}
<Relationship Id="rId${n + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`],
    ['xl/styles.xml', ESTILOS],
    ...hojas.map((h, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, hojaXml(h)]),
  ];
  return zipSinComprimir(ficheros.map(([nombre, xml]) => [nombre, enc.encode(xml)]));
}

// ── Zip «stored» ──────────────────────────────────────────────────

const TABLA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(datos: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < datos.length; i++) c = TABLA_CRC[(c ^ datos[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function zipSinComprimir(ficheros: [string, Uint8Array][]): Uint8Array {
  const locales: Uint8Array[] = [];
  const centrales: Uint8Array[] = [];
  let desplazamiento = 0;
  for (const [nombre, datos] of ficheros) {
    const n = enc.encode(nombre);
    const crc = crc32(datos);
    const local = new Uint8Array(30 + n.length);
    const l = new DataView(local.buffer);
    l.setUint32(0, 0x04034b50, true); l.setUint16(4, 20, true); l.setUint16(6, 0x0800, true);
    l.setUint16(8, 0, true); l.setUint16(10, 0, true); l.setUint16(12, 0x21, true); // 01/01/1980
    l.setUint32(14, crc, true); l.setUint32(18, datos.length, true); l.setUint32(22, datos.length, true);
    l.setUint16(26, n.length, true); l.setUint16(28, 0, true);
    local.set(n, 30);
    const central = new Uint8Array(46 + n.length);
    const c = new DataView(central.buffer);
    c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true);
    c.setUint16(8, 0x0800, true); c.setUint16(10, 0, true); c.setUint16(12, 0, true); c.setUint16(14, 0x21, true);
    c.setUint32(16, crc, true); c.setUint32(20, datos.length, true); c.setUint32(24, datos.length, true);
    c.setUint16(28, n.length, true); c.setUint32(42, desplazamiento, true);
    central.set(n, 46);
    locales.push(local, datos);
    centrales.push(central);
    desplazamiento += local.length + datos.length;
  }
  const tamCentral = centrales.reduce((s, x) => s + x.length, 0);
  const fin = new Uint8Array(22);
  const f = new DataView(fin.buffer);
  f.setUint32(0, 0x06054b50, true); f.setUint16(8, ficheros.length, true); f.setUint16(10, ficheros.length, true);
  f.setUint32(12, tamCentral, true); f.setUint32(16, desplazamiento, true);
  const partes = [...locales, ...centrales, fin];
  const total = new Uint8Array(partes.reduce((s, x) => s + x.length, 0));
  let o = 0;
  for (const p of partes) { total.set(p, o); o += p.length; }
  return total;
}
